import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { OptionsCluster, OptionsFlowFilters } from '@/types';

// Convert yyyymmdd to yyyy-mm-dd for Supabase query
function ymdToDate(ymd: string): string {
  if (ymd.length !== 8) return ymd;
  return `${ymd.slice(0, 4)}-${ymd.slice(4, 6)}-${ymd.slice(6, 8)}`;
}

// Convert yyyy-mm-dd to yyyymmdd for display
function dateToYmd(dateStr: string): string {
  return dateStr.replace(/-/g, '');
}

// Fetch options clusters with filters
export function useOptionsFlow(filters: OptionsFlowFilters) {
  return useQuery({
    queryKey: ['options-flow', filters],
    queryFn: async (): Promise<OptionsCluster[]> => {
      let query = supabase
        .from('options_clusters')
        .select('id, date, timestamp, symbol, underlying, expiry, strike, call_put, total_size, total_premium, vwap, cluster_type, dominant_sentiment')
        .order('total_premium', { ascending: false })
        .limit(500);

      // Filter by date if provided
      if (filters.date) {
        const dateStr = ymdToDate(filters.date);
        query = query.eq('date', dateStr);
      }

      // Filter by tickers if provided
      if (filters.tickers.length > 0) {
        query = query.in('underlying', filters.tickers);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Supabase error:', error);
        throw new Error('期权数据加载失败');
      }

      return (data || []) as OptionsCluster[];
    },
    enabled: !!filters.date,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// Fetch available dates for options flow data
export function useOptionsFlowDates() {
  return useQuery({
    queryKey: ['options-flow-dates'],
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase
        .from('options_clusters')
        .select('date')
        .order('date', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw new Error('日期加载失败');
      }

      // Get unique dates and convert to yyyymmdd format
      const uniqueDates = [...new Set((data || []).map((row: { date: string }) => dateToYmd(row.date)))];
      return uniqueDates;
    },
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
}

// Fetch distinct tickers from options clusters
export function useOptionsFlowTickers(date: string | null) {
  return useQuery({
    queryKey: ['options-flow-tickers', date],
    queryFn: async (): Promise<string[]> => {
      let query = supabase
        .from('options_clusters')
        .select('underlying');

      if (date) {
        const dateStr = ymdToDate(date);
        query = query.eq('date', dateStr);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Supabase error:', error);
        throw new Error('Ticker加载失败');
      }

      // Get unique tickers and sort
      const uniqueTickers = [...new Set((data || []).map((row: { underlying: string }) => row.underlying))].sort();
      return uniqueTickers;
    },
    enabled: !!date,
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}
